import { z } from 'zod'

// Workspace & User Enums
export const WorkspaceRole = z.enum(['WORKSPACE_OWNER', 'WORKSPACE_ADMIN', 'WORKSPACE_USER', 'WORKSPACE_GUEST']);
export type WorkspaceRole = z.infer<typeof WorkspaceRole>;

export const Theme = z.enum(['DARK', 'LIGHT']);
export type Theme = z.infer<typeof Theme>;

export const ProfileSetupStep = z.enum(['HOW_DID_YOU_HEAR_ABOUT_US', 'USING_US_FOR', 'ARE_YOU_USING_SOLO_OR_TEAM', 'MOST_EXCITED_FORM', 'COMPLETED']);
export type ProfileSetupStep = z.infer<typeof ProfileSetupStep>;

export const InvitationStatus = z.enum(['ACCEPTED', 'REVOKED', 'PENDING']);
export type InvitationStatus = z.infer<typeof InvitationStatus>;

// Contact & Conversation Enums
export const AvatarSVG = z.enum(['Man1', 'Female1', 'Man2', 'Female2', 'Man3', 'Female3', 'Man4', 'Female4', 'Man5', 'Female5', 'Man6', 'Female6', 'Man7', 'Female7', 'Man8', 'Female8']);
export type AvatarSVG = z.infer<typeof AvatarSVG>;

export const ConversationStatus = z.enum(['IN_PROGRESS', 'COMPLETED', 'ABANDONED']);
export type ConversationStatus = z.infer<typeof ConversationStatus>;

export const SentimentLevel = z.enum(['Negative', 'Neutral', 'Positive']);
export type SentimentLevel = z.infer<typeof SentimentLevel>;

export const ConversationRole = z.enum(['User', 'Assistant']);
export type ConversationRole = z.infer<typeof ConversationRole>;

// Media Enums
export const MediaProvider = z.enum(['UPLOADED', 'UNSPLASH', 'PEXEL']);
export type MediaProvider = z.infer<typeof MediaProvider>;

// Plan Enums
export const Plan = z.enum(['price_1QleESH1E5UsKynv0pTqCio2', 'price_1QleF4H1E5UsKynv5f0TeqXi', 'price_1QleH7H1E5UsKynvCeuMrm2m', 'price_1QleIOH1E5UsKynv9QLsNFOt', 'price_1P7w98H1E5UsKynvXQZy0L6c', 'price_1QnGa9H1E5UsKynvrl5eLdFJ', 'price_1QnGa9H1E5UsKynvCgBbYm2M', 'price_1QnGa9H1E5UsKynvmIKamOVR', 'price_1QnGa9H1E5UsKynv9vpVtuR8', 'price_1PNqi3H1E5UsKynvcHNBfqP8']);
export type Plan = z.infer<typeof Plan>;

// Form Enums
export const FormStatus = z.enum(['PUBLISHED', 'DRAFT']);
export type FormStatus = z.infer<typeof FormStatus>;

export const FontSizeSetting = z.enum(['EXTRA_SMALL', 'SMALL', 'MEDIUM', 'LARGE']);
export type FontSizeSetting = z.infer<typeof FontSizeSetting>;

export const FormType = z.enum(['MULTI_STEP', 'SINGLE_PAGE']);
export type FormType = z.infer<typeof FormType>;

export const FormElementType = z.enum(['ROUNDED', 'SQUARED', 'SEMI_ROUNDED']);
export type FormElementType = z.infer<typeof FormElementType>;

export const TransitionType = z.enum(['SLIDE_HORIZONTAL', 'SLIDE_VERTICAL']);
export type TransitionType = z.infer<typeof TransitionType>;

export const LogoPosition = z.enum([
  'TOP_LEFT',
  'TOP_CENTER',
  'TOP_RIGHT',
  'BOTTOM_LEFT',
  'BOTTOM_CENTER',
  'BOTTOM_RIGHT',
]);
export type LogoPosition = z.infer<typeof LogoPosition>;

export const GDPRSetting = z.enum(['ALWAYS', 'AUTO', 'NEVER']);
export type GDPRSetting = z.infer<typeof GDPRSetting>;

export const ContentAlignment = z.enum(['LEFT', 'CENTER', 'RIGHT']);
export type ContentAlignment = z.infer<typeof ContentAlignment>;

export const FormSnippetType = z.enum(['USER_CONTENT', 'WEBPAGE_CONTENT', 'DOCUMENT_CONTENT']);
export type FormSnippetType = z.infer<typeof FormSnippetType>;

export const FormJourneyQuestionType = z.enum(['OPEN_ENDED', 'MULTIPLE_CHOICE', 'STATEMENT', 'FILE', 'EMAIL', 'SHORT_TEXT', 'LONG_TEXT', 'OPTION_SCALE', 'PHONE_NUMBER', 'ADDRESS', 'WEBSITE', 'DROPDOWN', 'NUMBER', 'DATE', 'CALENDLY', 'SCHEDULER', 'RATING', 'RANKING', 'CONTACT_INFO', 'WELCOME_SCREEN', 'END_SCREEN', 'REDIRECT_SCREEN', 'NPS', 'MULTI_QUESTION_PAGE']);
export type FormJourneyQuestionType = z.infer<typeof FormJourneyQuestionType>;

export const CalendarProvider = z.enum(['CALENDLY', 'CALCOM', 'GOOGLE_CALENDAR']);
export type CalendarProvider = z.infer<typeof CalendarProvider>;

export const FormJourneyQuestionStrictness = z.enum(['STRICT', 'COMMON', 'CASUAL']);
export type FormJourneyQuestionStrictness = z.infer<typeof FormJourneyQuestionStrictness>;

export const JourneyItemMediaPosition = z.enum(['RANDOM', 'DETATCHED_LEFT', 'DETATCHED_RIGHT', 'SPLIT_LEFT', 'SPLIT_RIGHT', 'FULL', 'INLINE_LEFT', 'INLINE_RIGHT', 'INLINE_CENTER', 'INLINE_BELOW_TITLE', 'MOBILE_INLINE_CENTER', 'MOBILE_INLINE_TOP', 'MOBILE_POSTER_TOP', 'MOBILE_FULL']);
export type JourneyItemMediaPosition = z.infer<typeof JourneyItemMediaPosition>;

// Logic Enums
export const LogicConditionType = z.enum(['IF', 'ALWAYS']);
export type LogicConditionType = z.infer<typeof LogicConditionType>;

export const LogicConditionOperation = z.enum(['IS_EQUAL_TO', 'IS_NOT_EQUAL_TO', 'BEGINS_WITH', 'ENDS_WITH', 'CONTAINS', 'DOES_NOT_CONTAIN']);
export type LogicConditionOperation = z.infer<typeof LogicConditionOperation>;

export const LogicConditionAdditionalType = z.enum(['AND', 'OR']);
export type LogicConditionAdditionalType = z.infer<typeof LogicConditionAdditionalType>;

export const LogicActionType = z.enum(['GO_TO', 'ASSIGN_TO_SEGMENT', 'ADD', 'DIVIDE', 'MULTIPLY', 'SUBTRACT']);
export type LogicActionType = z.infer<typeof LogicActionType>;

// Report Enums
export const ReportJourneyItemType = z.enum(['TEXT', 'CHOICE']);
export type ReportJourneyItemType = z.infer<typeof ReportJourneyItemType>;

// Integration Enums
export const IntegrationType = z.enum(['ZAPIER']);
export type IntegrationType = z.infer<typeof IntegrationType>;

// Referral Enums
export const ReferralLinkStatus = z.enum(['ACTIVE', 'INACTIVE']);
export type ReferralLinkStatus = z.infer<typeof ReferralLinkStatus>;

export const ReferralLeadStatus = z.enum(['SIGNED_UP', 'CONVERTED']);
export type ReferralLeadStatus = z.infer<typeof ReferralLeadStatus>;

export const ReferralPayoutStatus = z.enum(['PENDING', 'APPROVED', 'PAID', 'CANCELLED']);
export type ReferralPayoutStatus = z.infer<typeof ReferralPayoutStatus>;

